import { ColDef, GridOptions, ICellRendererParams, IDetailCellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getActivityStateColor, getMaterialStateColor } from '../../../../utils/colors';
import { createDateTimeColumn, formatDateToDDMMYYYY, formatDateToYYYYMMDD, formatTime } from '../../../../utils/dates';
import {
    exportTypeEnum,
    getActivityStateInfos,
    getMaterialStateInfos,
    getTruckModeInfos,
    localStorageKeys,
    materialState,
    permissionLevels,
} from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import MachinesCell from '../../../cells/MachinesCell';
import RentalsCell from '../../../cells/RentalsCell';
import SiteCell from '../../../cells/SiteCell';
import StateCell from '../../../cells/StateCell';
import SubContractorsCell from '../../../cells/SubContractorsCell';
import TempWorkersCell from '../../../cells/TempWorkersCell';
import ToolsCell from '../../../cells/ToolsCell';
import LicensePlate from '../../../LicensePlate';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useTheme } from '../../../../hooks/useTheme';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import ExportCsvIcon from '../../../ExportCsvIcon';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { stepModeEnum } from '../../../../constants';

interface Props {
    dailyTrucksRides: DailyTruckRide[];
    activities: DataItem[];
    currentDate: Date;
    isLoading: boolean;
    stepMode: number;
}

const ActivitiesTable: React.FC<Props> = ({ activities, dailyTrucksRides, isLoading, currentDate, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { mode } = useTheme();
    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const columnDefs: ColDef<DataItem>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            colId: 'date',
            initialHide: true,
        },
        {
            cellStyle: (params) => {
                const rowIndex = params.node.rowIndex;
                if (rowIndex === null) return null;
                const api = params.api;

                const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                const prevId = prevRowNode?.data?.siteId._id;
                const nextId = nextRowNode?.data?.siteId._id;
                const currentId = params.data?.siteId._id;

                const marginTop = prevId !== currentId ? '5px' : '0';
                const marginBottom = nextId !== currentId ? '5px' : '0';

                const height = `calc(100% - ${parseInt(marginTop) + parseInt(marginBottom)}px)`;

                return {
                    padding: 0,
                    marginLeft: 5,
                    backgroundColor: params.data?.siteId.conducId?.color || 'transparent',
                    marginTop,
                    marginBottom,
                    height,
                    borderBottomLeftRadius: nextId !== currentId ? 10 : 0,
                    borderBottomRightRadius: nextId !== currentId ? 10 : 0,
                    borderTopLeftRadius: prevId !== currentId ? 10 : 0,
                    borderTopRightRadius: prevId !== currentId ? 10 : 0,
                    width: 20,
                };
            },
            colId: 'color',
            resizable: false,
            filter: false,
            sortable: false,
            minWidth: 30,
            initialWidth: 30,
            pinned: 'left',
            valueGetter: (params) => formatName(params.data?.siteId.conducId?.firstName, params.data?.siteId.conducId?.lastName),
            cellRenderer: () => '',
            enableRowGroup: false,
            suppressHeaderMenuButton: true,
            enableValue: false,
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteId.name',
            initialWidth: 300,
            pinned: 'left',
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                const rowIndex = params.node.rowIndex;
                if (rowIndex === null) return null;
                const api = params.api;

                const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                const prevId = prevRowNode?.data?.siteId._id;
                const nextId = nextRowNode?.data?.siteId._id;
                return params.data ? (
                    <SiteCell
                        activity={params.data}
                        disabled
                        remainingMaterial
                        previousSiteId={prevId}
                        nextSiteId={nextId}
                        currentDate={currentDate}
                    />
                ) : (
                    params.value
                );
            },
            colId: 'site',
        },
        {
            headerName: capitalizeFirstLetter(t('person')),
            valueGetter: (params) => formatName(params.data?.reportId.userId.firstName, params.data?.reportId.userId.lastName),
            colId: 'name',
            pinned: 'left',
            cellRenderer: 'agGroupCellRenderer',
            initialWidth: 250,
        },
        {
            headerName: capitalizeFirstLetter(t('start')),
            colId: 'start',
            initialWidth: 150,
            valueGetter: (params) => {
                if (!params.data) return null;
                return formatTime(new Date(params.data?.startTime));
            },
        },
        {
            headerName: capitalizeFirstLetter(t('end')),
            colId: 'end',
            initialWidth: 150,
            valueGetter: (params) => {
                if (!params.data?.validationTime) return null;
                return formatTime(new Date(params.data?.validationTime));
            },
        },
        createDateTimeColumn<DataItem>('validationTimestamp', 'validatedThe'),
        {
            headerName: capitalizeFirstLetter(t('machines')),
            colId: 'machines',
            initialWidth: 300,
            cellStyle: { overflow: 'visible' },
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyMachines.map((m) => m.machineId.designation),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return <MachinesCell activity={params.data} activityIndex={params.node.rowIndex} activities={[]} disabled createTransfer={false} />;
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('tools')),
            colId: 'tools',
            initialWidth: 300,
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyTools.map((t) => t.toolId.designation),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return <ToolsCell activity={params.data} activityIndex={params.node.rowIndex} activities={[]} disabled createTransfer={false} />;
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('rentals')),
            colId: 'rentals',
            initialWidth: 300,
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyRentals.map((r) => `${r.designation} ${r.rentalTypeId.designation}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return <RentalsCell activity={params.data} activityIndex={params.node.rowIndex} activities={[]} disabled createTransfer={false} />;
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },

        {
            headerName: capitalizeFirstLetter(t('observations')),
            field: 'observationsWorker',
            minWidth: 300,
            initialFlex: 1,
            colId: 'observations',
            valueFormatter: (params) => params.value || '',
            cellStyle: { whiteSpace: 'pre-wrap' },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('tempWorkers')),
            colId: 'tempworkers',
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) =>
                params.data?.dailyTempWorkers.map((tw) => `${tw.tempWorkerTypeId.designation} ${tw.durationHours}h ${tw.tempWorkerName}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return <TempWorkersCell activity={params.data} activityIndex={params.node.rowIndex} activities={[]} disabled />;
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('subContractors')),
            colId: 'subcontractors',
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailySubContractors.map((sc) => `${sc.subContractorTypeId.designation}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return <SubContractorsCell activity={params.data} activityIndex={params.node.rowIndex} activities={[]} disabled />;
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('truckManager')),
            field: 'isTruckManager',
            initialWidth: 100,
            colId: 'truckmanager',
        },
        {
            headerName: capitalizeFirstLetter(t('activity')),
            field: 'activityTypeId.designation',
            colId: 'activityTypeId',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getActivityStateInfos(params.data?.activityState))),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getActivityStateColor(params.data?.activityState)}
                        text={t(getActivityStateInfos(params.data?.activityState))}
                    />
                );
            },
            colId: 'status',
        },

        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<DataItem>[]),
    ];

    const masterColumnDefs: ColDef<DailyTruckRide>[] = [
        {
            headerName: '',
            valueGetter: 'node.rowIndex + 1',
            width: 60,
            suppressMovable: true,
            pinned: 'left',
            suppressHeaderMenuButton: true,
            colId: '#',
            cellStyle: () => ({ backgroundColor: mode === 'dark' ? 'black' : 'white' }),
        },
        {
            headerName: t('company'),
            valueGetter: (params) => params.data?.truckId?.truckCompanyId?.designation || '',
            colId: 'company',
        },
        {
            headerName: t('plateNumber'),
            field: 'truckId.plateNumber',
            cellRenderer: (params: ICellRendererParams<DailyTruckRide>) => {
                if (!params.value) return null;
                return <LicensePlate number={params.value} />;
            },
            colId: 'plateNumber',
        },

        {
            headerName: t('driver'),
            field: 'truckId.userId.firstName',
        },
        {
            headerName: t('truckType'),
            valueGetter: (params) => params.data?.truckId?.truckTypeId?.designation || '',
            colId: 'truckType',
        },
        {
            headerName: t('mode'),
            valueGetter: (params) => capitalizeFirstLetter(t(getTruckModeInfos(params.data?.mode))),
            colId: 'mode',
        },
        {
            headerName: `${t('source').charAt(0)}. ${t('loadNature')}`,
            valueGetter: (params) => params.data?.departureLoadNatureId?.designation || '',
            colId: 'departureLoadNature',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0)}. ${t('location')}`,
            valueGetter: (params) => params.data?.departureSiteId?.name || params.data?.departureQuarryId?.designation || '',
            colId: 'departureLocation',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0)}. ${t('dump')}`,
            valueGetter: (params) => params.data?.departureDumpId?.designation || '',
            colId: 'departureDump',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: `${t('source').charAt(0)}. ${t('tonnage')}`,
            field: 'departureTonnage',
            colId: 'departureTonnage',
            initialAggFunc: 'sum',
            headerStyle: { color: '#00b8c2' },
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return formatNumber(params.value, t('locale'), 2);
            },
        },
        {
            headerName: `${t('source').charAt(0)}. ${t('hour')}`,
            valueGetter: (params) => formatTime(params.data?.departureTime ? new Date(params.data?.departureTime) : undefined),
            colId: 'departureTime',
            headerStyle: { color: '#00b8c2' },
        },
        {
            headerName: t('siteHour'),
            valueGetter: (params) => formatTime(params.data?.time ? new Date(params.data?.time) : undefined),
            colId: 'time',
        },
        {
            headerName: `${t('destination').charAt(0)}. ${t('loadNature')}`,
            valueGetter: (params) => params.data?.arrivalLoadNatureId?.designation || '',
            colId: 'arrivalLoadNature',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0)}. ${t('location')}`,
            valueGetter: (params) => params.data?.arrivalSiteId?.name || params.data?.arrivalQuarryId?.designation || '',
            colId: 'arrivalLocation',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0)}. ${t('dump')}`,
            valueGetter: (params) => params.data?.arrivalDumpId?.designation || '',
            colId: 'arrivalDump',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: `${t('destination').charAt(0)}. ${t('tonnage')}`,
            field: 'arrivalTonnage',
            colId: 'arrivalTonnage',
            initialAggFunc: 'sum',
            headerStyle: { color: 'lightgreen' },
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return formatNumber(params.value, t('locale'), 2);
            },
        },
        {
            headerName: `${t('destination').charAt(0)}. ${t('hour')}`,
            valueGetter: (params) => formatTime(params.data?.arrivalTime ? new Date(params.data?.arrivalTime) : undefined),
            colId: 'arrivalTime',
            headerStyle: { color: 'lightgreen' },
        },
        {
            headerName: t('onSiteBackTime'),
            valueGetter: (params) => formatTime(params.data?.secondTime ? new Date(params.data?.secondTime) : undefined),
            colId: 'secondTime',
        },
        {
            headerName: t('transfers'),
            field: 'transfers',
            cellRenderer: (params: ICellRendererParams<DailyTruckRide>) => {
                if (!params.data) return null;
                return (
                    <div>
                        {(params.data?.transfers || []).map((transfer: any, i: number) => (
                            <div
                                key={i}
                                className="border-radius-10px pad-l-5px fx-c text-black"
                                style={{
                                    backgroundColor: getMaterialStateColor(transfer.state),
                                    marginTop: i !== 0 ? 5 : 0,
                                }}
                                title={
                                    `${transfer.machineId?.designation || transfer.toolId?.designation || `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}`} ` +
                                    `${t(getMaterialStateInfos(transfer.state))} ` +
                                    `${transfer.siteId?.name || transfer.quarryId?.designation}`
                                }
                            >
                                <div>
                                    {transfer.machineId?.designation ||
                                        transfer.toolId?.designation ||
                                        `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}`}
                                </div>
                                <div>
                                    <b>{transfer.state === materialState.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                    {transfer.siteId?.name || transfer.quarryId?.designation}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
            colId: 'transfers',
        },
        {
            headerName: t('observations'),
            field: 'observations',
            colId: 'observations',
            cellStyle: { whiteSpace: 'pre-wrap' },
        },
        {
            headerName: t('decommissioned'),
            field: 'isDecommissioned',
            colId: 'decommissioned',
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<DailyTruckRide>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={activities.sort((a, b) => {
                if (
                    formatName(a.siteId.conducId?.firstName, a.siteId.conducId?.lastName) <
                    formatName(b.siteId.conducId?.firstName, b.siteId.conducId?.lastName)
                )
                    return -1;
                if (
                    formatName(a.siteId.conducId?.firstName, a.siteId.conducId?.lastName) >
                    formatName(b.siteId.conducId?.firstName, b.siteId.conducId?.lastName)
                )
                    return 1;

                const siteA = a.siteId.name.toLowerCase();
                const siteB = b.siteId.name.toLowerCase();
                if (siteA < siteB) return -1;
                if (siteA > siteB) return 1;

                if (a.isTruckManager) return -1;
                if (b.isTruckManager) return 1;

                if (a.reportId.userId.profileId.permsLevel < b.reportId.userId.profileId.permsLevel) return -1;
                if (a.reportId.userId.profileId.permsLevel > b.reportId.userId.profileId.permsLevel) return 1;

                return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
            })}
            columnDefs={columnDefs}
            agGridProps={{
                getRowId: ({ data }) => data._id,
                autoGroupColumnDef: {
                    pinned: 'left',
                },
                rowGroupPanelShow: 'always',
                masterDetail: true,
                isRowMaster: (dataItem) => dailyTrucksRides.some((item) => item.activityId === dataItem._id),
                detailCellRendererParams: {
                    detailGridOptions: {
                        columnDefs: masterColumnDefs,
                        defaultColDef: { autoHeight: true },
                        localeText: AG_GRID_LOCALE_FR as {
                            [key: string]: string;
                        },
                    },
                    getDetailRowData: (params) => {
                        params.successCallback(
                            dailyTrucksRides
                                .filter((item) => item.activityId === params.data._id)
                                .sort((a, b) => (a.time ?? '').localeCompare(b.time || ''))
                        );
                    },
                } as IDetailCellRendererParams<DataItem>,
            }}
            loading={isLoading}
            exportCsvFileName={'supervision-activities-' + formatDateToYYYYMMDD(currentDate)}
            storageKey={localStorageKeys.TABLE.OPERATIONS.SUPERVISION.ACTIVITIES}
            headerRightComponent={
                <>
                    <ExportCsvIcon
                        initDate={currentDate}
                        onExport={(startDate, endDate, fileName, delimiter) =>
                            exportCsv({ exportType: exportTypeEnum.ACTIVITIES, queries: { startDate, endDate, delimiter, fileName } })
                        }
                        exportIsLoading={isExportCsvLoading}
                        subTitle={capitalizeFirstLetter(t('activities'))}
                    />
                </>
            }
        />
    );
};

export default ActivitiesTable;
